import { useCallback, useRef, useState } from "react";
import { ref, uploadString } from "@firebase/storage";
import Compress from "react-image-file-resizer";
import { useStorage } from "reactfire";

import { useTranslator } from "@/i18n/useTranslator";

import { DsButton, DsText } from "./ui";

type FileType = {
  src: string;
  ref: ReturnType<typeof ref>;
  name: string;
};

type UploadFilePropsType = {
  basePath: string;
  baseName: string;
  multiple?: boolean;
  onUploadComplete: (path: string | string[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
};

export const UploadFile = ({
  basePath,
  baseName,
  multiple = false,
  onUploadComplete,
  maxFiles = 10,
  maxSizeMB = 25,
}: UploadFilePropsType) => {
  const storage = useStorage();
  const filesInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileType[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslator();

  const resetForm = useCallback((): void => {
    if (filesInputRef.current) {
      filesInputRef.current.value = "";
    }
    setFiles([]);
    setIsUploading(false);
    setError(null);
  }, []);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSizeMB * 1024 * 1024) {
        return "File too large";
      }
      if (!file.type.startsWith("image/")) {
        return "Invalid file type";
      }
      return null;
    },
    [maxSizeMB],
  );

  const handleUpload = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();
      if (files.length === 0 || isUploading) return;

      setIsUploading(true);
      setError(null);

      try {
        if (multiple) {
          // Handle multiple file upload with better error handling
          const uploadPromises = files.map(async (file, index) => {
            try {
              const snapshot = await uploadString(
                file.ref,
                file.src,
                "data_url",
              );
              return {
                status: "fulfilled" as const,
                value: snapshot.ref.fullPath,
                index,
              };
            } catch (error) {
              console.error(`Error uploading file ${index}:`, error);
              return {
                status: "rejected" as const,
                reason: error,
                index,
              };
            }
          });

          const results = await Promise.all(uploadPromises);
          const successfulUploads = results
            .filter(
              (
                result,
              ): result is {
                status: "fulfilled";
                value: string;
                index: number;
              } => result.status === "fulfilled",
            )
            .map(result => result.value);

          if (successfulUploads.length > 0) {
            onUploadComplete(successfulUploads);
            resetForm();
          } else {
            setError("All file uploads failed");
            setIsUploading(false);
          }
        } else {
          // Handle single file upload
          const snapshot = await uploadString(
            files[0].ref,
            files[0].src,
            "data_url",
          );
          onUploadComplete(snapshot.ref.fullPath);
          resetForm();
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        setError("Upload failed");
        setIsUploading(false);
      }
    },
    [files, isUploading, multiple, onUploadComplete, resetForm],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const selectedFiles = e.target.files;
      if (!selectedFiles) return;

      // Validate file count
      if (!multiple && selectedFiles.length > 1) {
        setError("Only one file allowed");
        return;
      }

      if (selectedFiles.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      setFiles([]);
      setError(null);

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles.item(i);
        if (!file) continue;

        // Validate file
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          continue;
        }

        Compress.imageFileResizer(
          file,
          1920,
          1920,
          "JPEG",
          80,
          0,
          uri => {
            if (typeof uri === "string") {
              setFiles(oldFiles => [
                ...oldFiles,
                {
                  src: uri,
                  name: file.name,
                  ref: ref(
                    storage,
                    `${basePath}/${baseName}-${Date.now()}-${oldFiles.length}.jpg`,
                  ),
                },
              ]);
            }
          },
          "base64",
        );
      }
    },
    [basePath, baseName, maxFiles, multiple, storage, validateFile],
  );
  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      void handleUpload(e);
    },
    [handleUpload],
  );

  return (
    <div>
      {/* Error display */}
      {error && (
        <DsText variant="errorText" className="mb-2 text-red-600">
          {error}
        </DsText>
      )}

      <div className="flex gap-4 mb-2 overflow-x-auto">
        {files.map((file, index) => (
          <div key={index} className="flex-shrink-0">
            <img
              src={file.src}
              alt={file.name}
              className="w-24 h-24 object-cover rounded border"
            />
            <DsText
              variant="body"
              className="text-xs text-center mt-1 truncate w-24">
              {file.name}
            </DsText>
          </div>
        ))}
      </div>

      <input
        ref={filesInputRef}
        type="file"
        name="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleChange}
        disabled={isUploading}
        className="block mb-4 w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
      />

      {files.length > 0 && (
        <DsButton
          onClick={handleButtonClick}
          disabled={isUploading}
          variant="custom"
          size="custom"
          className={`w-full shadow rounded py-2 font-bold uppercase transition-colors ${
            isUploading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#ffe502] text-gray-600 hover:bg-yellow-400"
          }`}>
          {isUploading ? t("loading") : t("add_new_job_drawer.upload_photos")}
        </DsButton>
      )}
    </div>
  );
};
